import { ActionTree, GetterTree, MutationTree } from 'vuex';
import HomeSmartApi from '@/plugins/HomeSmartApi';
import { Counter } from '@/modules/backend/Entities/Counter';
import { EditCounterData } from '@/modules/backend/Dto/EditCounterData';
import { CreateCounterData } from '@/modules/backend/Dto/CreateCounterData';

interface State {
  Counters: {
    [id: number]: Counter[];
  };
}

export const storeState: State = {
  Counters: {}
};

export const getters: GetterTree<State, unknown> = {
  CounterById: (store) => (counterId: number): Counter | undefined => {
    return Object.entries(store.Counters).reduce((accum: Counter | undefined, [, items]) => {
      if (accum) {
        return accum;
      }

      return items.find((counter) => counter.getId() === counterId);
    }, undefined);
  },

  Counters: (store) => Object.values(store.Counters).flat(),

  CountersByAccountId: (store) => (accountId: number): Counter[] => store.Counters[accountId]
};

export const mutations: MutationTree<State> = {
  Counters: (state, payload: Counter[]) => {
    const items = payload.reduce((accum, item) => {
      accum[item.getAccount().getId()] = [
        ...accum[item.getAccount().getId()] || [],
        item
      ];

      return accum;
    }, {} as {[id: number]: Counter[]});

    state.Counters = { ...state.Counters, ...items };
  },
  Add: (state, payload: Counter) => {
    if (!Object.prototype.hasOwnProperty.call(state.Counters, payload.getAccount().getId())) {
      state.Counters = {
        ...state.Counters,
        [payload.getAccount().getId()]: [payload]
      };
      return;
    }

    state.Counters = {
      ...state.Counters,
      [payload.getAccount().getId()]: [
        ...state.Counters[payload.getAccount().getId()],
        payload
      ]
    };
  },
  Replace: (state, counter: Counter) => {
    state.Counters = Object.entries(state.Counters).reduce((accum, [accountId, items]) => {
      accum[Number(accountId)] = items.map((item) => {
        if (item.getId() === counter.getId()) {
          return counter;
        }
        return item;
      });

      return accum;
    }, {} as {[id: number]: Counter[]});
  }
};

export const actions: ActionTree<State, unknown> = {
  async Fetch({ commit }, accountId: number) {
    const items = await HomeSmartApi.Counters.Fetch(accountId);
    commit('Counters', items);
  },

  async Create({ commit }, payload: CreateCounterData) {
    const counter = await HomeSmartApi.Counters.Create(payload);
    commit('Add', counter);
  },

  async GetOne({ commit }, counterId: number) {
    const singleCounter: Counter = await HomeSmartApi.Counters.GetOne(counterId);
    commit('Add', singleCounter);
  },

  async Edit({ commit }, payload: EditCounterData) {
    const counter: Counter = await HomeSmartApi.Counters.Edit(payload);
    commit('Replace', counter);
    return counter;
  }
};

export const counters = {
  namespaced: true,
  state: storeState,
  actions,
  getters,
  mutations
};
